function renderMultiSelectFieldStaticOptionsTemplate({
  staticOptions,
  indent
}: {
  staticOptions: string
  indent: string
}): string {
  return `const staticOptions = ${staticOptions}
${indent}    const options = staticOptions`
}

function renderMultiSelectFieldContentTemplate({
  indent,
  lowerLabel,
  searchVar,
  setSearchVar
}: {
  indent: string
  lowerLabel: string
  searchVar: string
  setSearchVar: string
}): string {
  return `<CommandInput
${indent}                placeholder="Search ${lowerLabel}..."
${indent}                value={${searchVar}}
${indent}                onValueChange={${setSearchVar}}
${indent}              />`
}

function renderMultiSelectFieldCreateLabelTemplate({
  indent,
  searchVar
}: {
  indent: string
  searchVar: string
}): string {
  return `
${indent}    const createLabel = ${searchVar}.trim()
${indent}    const canCreateOption =
${indent}      createLabel.length > 0 &&
${indent}      !options.some(
${indent}        (option) =>
${indent}          option.label.toLowerCase() === createLabel.toLowerCase() ||
${indent}          option.value.toLowerCase() === createLabel.toLowerCase()
${indent}      )
`
}

function renderMultiSelectFieldActionButtonsTemplate({
  indent,
  pendingVar,
  createHandler
}: {
  indent: string
  pendingVar: string
  createHandler: string
}): string {
  return `
${indent}              {canCreateOption ? (
${indent}                <>
${indent}                  <CommandSeparator />
${indent}                  <div className="p-1">
${indent}                    <Button
${indent}                      type="button"
${indent}                      variant="ghost"
${indent}                      className="w-full justify-start"
${indent}                      disabled={${pendingVar}}
${indent}                      aria-busy={${pendingVar}}
${indent}                      onClick={() => ${createHandler}(createLabel)}
${indent}                    >
${indent}                      <Plus className="size-4" />
${indent}                      {${pendingVar} ? 'Creating…' : \`Create "\${createLabel}"\`}
${indent}                    </Button>
${indent}                  </div>
${indent}                </>
${indent}              ) : null}`
}

function renderMultiSelectFieldActionButtonsSerializedValueTemplate({
  indent,
  fieldName,
  optionsId,
  optionsDeclaration,
  createState,
  previewLimit,
  formItemProps,
  labelWithDescription,
  openVar,
  setOpenVar,
  stringify,
  searchInput,
  lowerLabel,
  defaultValueFromHandlerContent,
  clearSearch,
  createAction
}: {
  indent: string
  fieldName: string
  optionsId: string
  optionsDeclaration: string
  createState: string
  previewLimit: number
  formItemProps: string
  labelWithDescription: string
  openVar: string
  setOpenVar: string
  stringify: string
  searchInput: string
  lowerLabel: string
  defaultValueFromHandlerContent: string
  clearSearch: string
  createAction: string
}): string {
  return `${indent}<FormField
${indent}  control={form.control}
${indent}  name="${fieldName}"
${indent}  render={({ field: formField }) => {
${indent}    ${optionsDeclaration}
${indent}    const selectedValues = Array.isArray(formField.value) ? formField.value : []
${indent}    const selectedOptions = options.filter((option) => selectedValues.includes(option.value))${createState}
${indent}    const previewLimit = ${previewLimit}
${indent}    const shouldCollapseSelectedOptions =
${indent}      selectedOptions.length > Math.max(previewLimit, 2)
${indent}    const visibleSelectedOptions = shouldCollapseSelectedOptions
${indent}      ? selectedOptions.slice(0, previewLimit)
${indent}      : selectedOptions
${indent}    const hiddenSelectedOptions = shouldCollapseSelectedOptions
${indent}      ? selectedOptions.slice(previewLimit)
${indent}      : []
${indent}    return (
${indent}      <FormItem${formItemProps}>
${indent}        ${labelWithDescription}
${indent}        <Popover open={${openVar}} onOpenChange={${setOpenVar}}>
${indent}          <PopoverTrigger
${indent}            render={
${indent}              <FormControl>
${indent}                <Button
${indent}                  type="button"
${indent}                  variant="outline"
${indent}                  role="combobox"
${indent}                  aria-expanded={${openVar}}
${indent}                  aria-controls="${optionsId}"
${indent}                  className={cn("w-full justify-between gap-2", {
${indent}                    "text-muted-foreground": selectedOptions.length === 0
${indent}                  })}
${indent}                  disabled={isPending}
${indent}                >
${indent}                <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
${indent}                  {selectedOptions.length > 0 ? (
${indent}                    <>
${indent}                      <span className="truncate">
${indent}                        {visibleSelectedOptions.map((option) => option.label).join(', ')}
${indent}                      </span>
${indent}                      {hiddenSelectedOptions.length > 0 ? (
${indent}                        <Tooltip>
${indent}                          <TooltipTrigger
${indent}                            render={<Badge variant="outline" className="h-5 shrink-0 tabular-nums" />}
${indent}                          >
${indent}                              {'+'}
${indent}                              {hiddenSelectedOptions.length}{' '}
${indent}                              {hiddenSelectedOptions.length === 1 ? 'other' : 'others'}
${indent}                          </TooltipTrigger>
${indent}                          <TooltipContent align="center" side="bottom" className="max-w-60 w-full">
${indent}                            <div className="space-y-1">
${indent}                              {hiddenSelectedOptions.map((option) => (
${indent}                                <div key={option.value} className="truncate">
${indent}                                  {option.label}
${indent}                                </div>
${indent}                              ))}
${indent}                            </div>
${indent}                          </TooltipContent>
${indent}                        </Tooltip>
${indent}                      ) : null}
${indent}                    </>
${indent}                  ) : (
${indent}                    <span className="truncate">{${stringify}}</span>
${indent}                  )}
${indent}                </span>
${indent}                <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
${indent}                </Button>
${indent}              </FormControl>
${indent}            }
${indent}          />
${indent}          <PopoverContent id="${optionsId}" className="w-(--anchor-width) p-0" align="start">
${indent}            <Command>
${indent}              ${searchInput}
${indent}              <CommandList>
${indent}                <CommandEmpty>No ${lowerLabel} found.</CommandEmpty>
${indent}                <CommandGroup>
${indent}                  {options.map((option) => {
${indent}                    const isSelected = selectedValues.includes(option.value)
${indent}                    return (
${indent}                      <CommandItem
${indent}                        key={option.value}
${indent}                        value={\`\${option.label} \${option.value}\`}
${indent}                        onSelect={() => {
${indent}                          const nextValues = isSelected
${indent}                            ? selectedValues.filter((value: string) => value !== option.value)
${indent}                            : [...selectedValues, option.value]
${defaultValueFromHandlerContent}${indent}                          formField.onChange(nextValues)${clearSearch}
${indent}                        }}
${indent}                      >
${indent}                        <Check className={cn("size-4", {
${indent}                          "opacity-100": isSelected,
${indent}                          "opacity-0": !isSelected
${indent}                        })} />
${indent}                        <span className="truncate">{option.label}</span>
${indent}                      </CommandItem>
${indent}                    )
${indent}                  })}
${indent}                </CommandGroup>
${indent}              </CommandList>${createAction}
${indent}            </Command>
${indent}          </PopoverContent>
${indent}        </Popover>
${indent}        <FormMessage />
${indent}      </FormItem>
${indent}    )
${indent}  }}
${indent}/>`
}

export function renderMultiSelectFieldRenderer(
  request:
    | {
        kind: 'renderMultiSelectFieldStaticOptionsTemplate'
        props: {
          staticOptions: string
          indent: string
        }
      }
    | {
        kind: 'renderMultiSelectFieldContentTemplate'
        props: {
          indent: string
          lowerLabel: string
          searchVar: string
          setSearchVar: string
        }
      }
    | {
        kind: 'renderMultiSelectFieldCreateLabelTemplate'
        props: {
          indent: string
          searchVar: string
        }
      }
    | {
        kind: 'renderMultiSelectFieldActionButtonsTemplate'
        props: {
          indent: string
          pendingVar: string
          createHandler: string
        }
      }
    | {
        kind: 'renderMultiSelectFieldActionButtonsSerializedValueTemplate'
        props: {
          indent: string
          fieldName: string
          optionsId: string
          optionsDeclaration: string
          createState: string
          previewLimit: number
          formItemProps: string
          labelWithDescription: string
          openVar: string
          setOpenVar: string
          stringify: string
          searchInput: string
          lowerLabel: string
          defaultValueFromHandlerContent: string
          clearSearch: string
          createAction: string
        }
      }
): string {
  switch (request.kind) {
    case 'renderMultiSelectFieldStaticOptionsTemplate':
      return renderMultiSelectFieldStaticOptionsTemplate(request.props)
    case 'renderMultiSelectFieldContentTemplate':
      return renderMultiSelectFieldContentTemplate(request.props)
    case 'renderMultiSelectFieldCreateLabelTemplate':
      return renderMultiSelectFieldCreateLabelTemplate(request.props)
    case 'renderMultiSelectFieldActionButtonsTemplate':
      return renderMultiSelectFieldActionButtonsTemplate(request.props)
    case 'renderMultiSelectFieldActionButtonsSerializedValueTemplate':
      return renderMultiSelectFieldActionButtonsSerializedValueTemplate(request.props)
  }

  throw new Error('Unsupported renderer request')
}
